import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  Assessment,
  Summarize,
  Build,
  ChangeCircle,
  Apartment as ApartmentIcon,
  Support as SupportIcon,
  PersonPin,
  Description as DescriptionIcon,
  ChevronLeft,
  Chat,
  LockOutlined,
  MoveUp,
} from "@mui/icons-material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";

// Custom styled components
const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? 240 : 60,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: open ? 240 : 60,
    boxSizing: "border-box",
    background: "#009797",
    boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.25)",
    transition: theme.transitions.create(["width", "transform"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },
}));

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  marginBottom: theme.spacing(2),
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  "& .MuiListItemText-primary": {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: selected ? 600 : 400,
    color: "#FFFFFF",
  },
  "& .MuiListItemText-secondary": {
    fontFamily: "Poppins",
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiListItemIcon-root": {
    minWidth: 40,
    color: "#FFFFFF",
    "& img": {
      width: 24,
      height: 24,
      filter: "brightness(0) invert(1)",
    },
  },
}));

// Define minimum required tier for each item
// 0 = Free, 1 = Standard, 2 = Platinum
const menuItemsConfig = [
  {
    text: "Dashboard",
    icon: <Dashboard />,
    pathSuffix: "dashboard",
    minTier: 0,
  },
  {
    text: "Career Report",
    icon: <Assessment />,
    pathSuffix: "report",
    minTier: 0,
  },
  {
    text: "Career Summary",
    icon: <Summarize />,
    pathSuffix: "summary",
    minTier: 0,
  },
  {
    text: "Ask CoachCAROL",
    icon: <Chat />,
    pathSuffix: "ai-chat",
    minTier: 2, // Platinum Only
  },

  {
    text: "Job Hunting Toolkit",
    icon: <Build />,
    pathSuffix: "cheat-sheets",
    minTier: 1,
  },
  {
    text: "Making Your Mid-Career Switch",
    icon: <ChangeCircle />,
    pathSuffix: "mid-career-switching",
    minTier: 1,
  },
  {
    text: "Career Transitions",
    icon: <MoveUp />,
    pathSuffix: "career-transitions",
    minTier: 0,
  },
  {
    text: "Land Your Dream Job",
    icon: <ApartmentIcon />,
    pathSuffix: "land-your-dream-job",
    minTier: 1,
  },
  {
    text: "Resume Samples",
    icon: <DescriptionIcon />,
    pathSuffix: "resume-templates",
    minTier: 1,
  },

  {
    text: "Personalised Coaching",
    icon: <PersonPin />,
    pathSuffix: "special-coaching",
    minTier: 0,
  },
  {
    text: "Support",
    icon: <SupportIcon />,
    pathSuffix: "support",
    minTier: 0,
  },
];

const NavigationSidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  const handleItemClick = (item) => {
    navigate(`/${item.pathSuffix}/${userId}`);
    if (isMobile) setOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button - Always visible on mobile */}
      {isMobile && !open && (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            left: 16,
            top: 16,
            zIndex: theme.zIndex.drawer + 2,
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
            pr: 1,
          }}
        >
          <IconButton
            onClick={() => setOpen(!open)}
            sx={{
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>

        <List sx={{ mt: 2 }}>
          {menuItemsConfig.map((item) => (
            <StyledListItem
              button
              key={item.text}
              onClick={() => handleItemClick(item)}
              selected={location.pathname.startsWith(
                `/${item.pathSuffix}/${userId}`
              )}
              sx={{
                opacity: 1,
                cursor: "pointer",
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              {open && <ListItemText primary={item.text} />}
            </StyledListItem>
          ))}
        </List>
      </StyledDrawer>
    </>
  );
};

export default NavigationSidebar;
